import { formatDisplayValue } from '../constants/complianceSectionSchemas';

export function normalizeToArray(val) {
  if (val === null || val === undefined || val === '') return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed.startsWith('[')) {
      try {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr)) return arr;
      } catch {
        /* fall through */
      }
    }
    return [trimmed];
  }
  return [val];
}

/** Parse a checklist date from strings, arrays, or {date: ...} objects. */
export function parseChecklistDate(val) {
  if (val === null || val === undefined || val === '') return null;
  if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;

  if (typeof val === 'object' && !Array.isArray(val)) {
    const nested = val.date ?? val.meeting_date ?? val.value ?? val.iep_date;
    if (nested != null && nested !== val) return parseChecklistDate(nested);
    return null;
  }

  if (Array.isArray(val)) {
    const parsed = val.map(parseChecklistDate).filter(Boolean);
    if (!parsed.length) return null;
    parsed.sort((a, b) => b - a);
    return parsed[0];
  }

  if (typeof val !== 'string') return null;
  const s = val.trim();
  if (!s) return null;

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(+iso[1], +iso[2] - 1, +iso[3]);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const mmdd = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmdd) {
    const d = new Date(+mmdd[3], +mmdd[1] - 1, +mmdd[2]);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** YYYY-MM-DD for HTML date inputs. */
export function toISODateForInput(val) {
  const d = parseChecklistDate(val);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Human-readable single date for checklist headers and labels. */
export function formatChecklistDate(val) {
  const d = parseChecklistDate(val);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Additional dates when a field contains multiple values (newest first). */
export function formatChecklistDateList(val) {
  const items = normalizeToArray(val);
  if (items.length <= 1) return null;
  const parsed = items
    .map((raw) => ({ raw, d: parseChecklistDate(raw) }))
    .filter((x) => x.d);
  if (parsed.length <= 1) return null;
  parsed.sort((a, b) => b.d - a.d);
  return parsed.map((x) =>
    x.d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  );
}

export function formatDob(val) {
  const d = parseChecklistDate(val);
  if (!d) return 'N/A';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${d.getFullYear()}`;
}

/** Resolve DOB from checklist header, student profile, or synced section details. */
export function resolveChecklistDob(header, student, sectionDetails) {
  const identity = sectionDetails?.identity_timeline?.extracted_data;
  const candidates = [
    header?.dob,
    student?.date_of_birth,
    identity?.date_of_birth,
    identity?.dob,
  ];
  for (const candidate of candidates) {
    const formatted = formatDob(candidate);
    if (formatted && formatted !== 'N/A') return formatted;
  }
  return 'N/A';
}

/** Store date from date input as MM/DD/YYYY (checklist convention). */
export function toStoredChecklistDate(isoDate) {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[1]}/${parts[2]}/${parts[0]}`;
}

export function formatKey(key) {
  if (key === 'accommodations_table1') return 'Accommodations Table';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function isObjectList(arr) {
  return arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null && !Array.isArray(arr[0]);
}

/** Readable value for tooltip extracted-data fields. */
export function formatExtractedFieldValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    if (!value.length) return null;
    if (isObjectList(value)) {
      return value
        .map((item, i) => {
          if (!item || typeof item !== 'object') return formatDisplayValue(item);
          const rtype = String(item.responder_type || '').toLowerCase();
          const tool = item.test_name || item.tool || item.assessment || '';
          if (rtype && tool) return `${rtype === 'parent' ? 'Parent' : rtype === 'teacher' ? 'Teacher' : rtype} ${tool}`;
          if (item.responder && tool) return `${item.responder} — ${tool}`;
          const bits = Object.entries(item)
            .filter(([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object')
            .map(([k, v]) => `${formatKey(k)}: ${formatDisplayValue(v)}`);
          return bits.length ? bits.join(' · ') : `${formatKey(Object.keys(item)[0] || 'item')} ${i + 1}`;
        })
        .filter(Boolean)
        .join('; ');
    }
    return value.map((item) => formatDisplayValue(item)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(
      ([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object'
    );
    if (entries.length) {
      return entries.map(([k, v]) => `${formatKey(k)}: ${formatDisplayValue(v)}`).join(' · ');
    }
    const nested = formatDisplayValue(value);
    return nested && nested !== '[object Object]' ? nested : null;
  }
  const text = String(value).trim();
  return text || null;
}

export function coerceSentComplete(val) {
  return val === true;
}

/** Known raw pipeline codes → plain staff sentences (safety net). */
const HR_CODE_MESSAGES = {
  behavioral_scores_unverified: 'Behavioral scores could not be verified from the document',
  cognitive_scores_unverified: 'Cognitive scores could not be verified from the document',
  academic_scores_unverified: 'Academic scores could not be verified from the document',
  adaptive_scores_unverified: 'Adaptive scores could not be verified from the document',
  evaluation_scores_out_of_range_dropped:
    'Some assessment scores looked invalid and were removed; confirm from the score report',
  lre_justification_no_evidence: 'LRE justification could not be verified from the document',
  goals_partial_no_evidence: 'Some goals could not be verified from the document and were removed',
  services_partial_no_evidence: 'Some services could not be verified from the document and were removed',
  participation_partial_no_evidence:
    'Some assessment participation rows could not be verified and were removed',
  insufficient_evidence: 'Not enough evidence in the document to confirm this item',
};

const HR_TECH_TOKEN_RE = /^[a-z][a-z0-9_]*(?:\[[0-9]+\])?(?:\.[a-z][a-z0-9_]*)?$/i;
const HR_INDEXED_NO_EVIDENCE_RE =
  /^([a-z][a-z0-9_]*)(?:\[\d+\])?\.([a-z][a-z0-9_]*)_no_evidence$/i;
const HR_FIELD_NO_EVIDENCE_RE = /^([a-z][a-z0-9_]*)_no_evidence$/i;

function labelizeHrToken(token) {
  return String(token || '').replace(/_/g, ' ').trim() || 'item';
}

export function humanizeReviewMessage(code, message) {
  const codeS = String(code || '').trim();
  const msgS = String(message || '').trim();
  let candidate = msgS || codeS;
  // Only rewrite when displayed text itself is a raw token.
  let lookUp = (candidate && HR_TECH_TOKEN_RE.test(candidate)) ? candidate : '';
  if (lookUp) {
    const mapped = HR_CODE_MESSAGES[lookUp] || HR_CODE_MESSAGES[lookUp.toLowerCase()];
    if (mapped) return mapped;
    if (/^[a-z][a-z0-9_]*_risky_accommodation_no_evidence$/i.test(lookUp)) {
      return 'An accommodation could not be verified on the accommodations table';
    }
    const indexed = lookUp.match(HR_INDEXED_NO_EVIDENCE_RE);
    if (indexed) {
      const field = labelizeHrToken(indexed[1]);
      const attr = String(indexed[2] || '').toLowerCase();
      if (attr === 'test_name') {
        return `${field} name could not be verified from the document`;
      }
      return `${field} ${labelizeHrToken(attr)} could not be verified from the document`;
    }
    const bare = lookUp.match(HR_FIELD_NO_EVIDENCE_RE);
    if (bare) {
      return `${labelizeHrToken(bare[1])} could not be verified from the document`;
    }
    if (lookUp.includes('_') && !lookUp.includes(' ')) {
      const labeled = labelizeHrToken(lookUp);
      return labeled.charAt(0).toUpperCase() + labeled.slice(1);
    }
  }
  candidate = candidate
    .replace(/\bblank Marked on Data Review\s*\/\s*Marked on PWN\b/gi, 'Data Review / PWN not marked')
    .replace(
      /\bPWN\s*\/\s*Data Review\s*\/\s*date fields are incomplete\b/gi,
      'Data Review / PWN / testing date incomplete',
    )
    .replace(/\s*[—–-]\s*human review required\.?\s*$/i, '')
    .trim();
  return candidate;
}

/** Display text for a human_review_flags entry (string or object). */
export function humanReviewFlagText(flag) {
  if (flag == null) return '';
  if (typeof flag === 'string') {
    const stripped = flag.replace(/^\[[A-Z0-9_]+\]\s*/, '').trim();
    return humanizeReviewMessage(stripped, stripped);
  }
  if (typeof flag !== 'object') return String(flag);
  return humanizeReviewMessage(
    flag.code,
    flag.message || flag.reason || flag.description || '',
  ).trim();
}

function _normReviewText(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function _humanReviewMatchKeys(flag) {
  if (flag == null) return [];
  if (typeof flag === 'string') {
    const keys = [flag, humanReviewFlagText(flag)];
    const bracket = flag.match(/^\[([A-Z0-9_]+)\]/);
    if (bracket) keys.push(bracket[1]);
    return keys.filter(Boolean);
  }
  if (typeof flag !== 'object') return [String(flag)];
  return [flag.message, flag.reason, flag.description, flag.code].filter(Boolean);
}

/** True when staff already dismissed this HR flag (message or code). */
export function isHumanReviewDismissed(flag, dismissedFindings) {
  const dismissed = new Set(
    (dismissedFindings || []).map((s) => _normReviewText(s)).filter(Boolean),
  );
  if (!dismissed.size) return false;
  return _humanReviewMatchKeys(flag).some((k) => dismissed.has(_normReviewText(k)));
}

export function filterDismissedHumanReviewFlags(flags, dismissedFindings) {
  if (!Array.isArray(flags) || !flags.length) return [];
  return flags.filter((f) => !isHumanReviewDismissed(f, dismissedFindings));
}

/** Payload for updateSection dismiss_finding (exact flag text only). */
export function humanReviewDismissPayload(flag) {
  if (flag == null) return null;
  if (typeof flag === 'string') {
    const trimmed = flag.trim();
    return trimmed ? { dismiss_finding: trimmed } : null;
  }
  const message = String(
    flag.message || flag.reason || flag.description || flag.code || '',
  ).trim();
  if (!message) return null;
  return { dismiss_finding: message };
}

/**
 * Drop HR flags already surfaced as findings (same validator_code or same message).
 * Display-only: API still returns both lists for counts / dismiss.
 */
export function filterHumanReviewFlagsNotInFindings(flags, findings) {
  const list = Array.isArray(flags) ? flags : [];
  if (!list.length) return [];
  const findingsList = Array.isArray(findings) ? findings : [];

  const codes = new Set();
  const descs = new Set();
  for (const f of findingsList) {
    if (!f || typeof f !== 'object') continue;
    if (f.validator_code) codes.add(String(f.validator_code));
    if (f.description) descs.add(_normReviewText(f.description));
  }
  if (!codes.size && !descs.size) return list;

  return list.filter((flag) => {
    if (typeof flag === 'string') {
      const bracket = flag.match(/^\[([A-Z0-9_]+)\]/);
      if (bracket && codes.has(bracket[1])) return false;
      const t = _normReviewText(humanReviewFlagText(flag));
      return t ? !descs.has(t) : true;
    }
    if (flag && typeof flag === 'object') {
      if (flag.code && codes.has(String(flag.code))) return false;
      const t = _normReviewText(humanReviewFlagText(flag));
      if (t && descs.has(t)) return false;
    }
    return true;
  });
}

/** Fix malformed sync payloads (e.g. sent_complete or value stored as []). */
export function normalizeChecklistResponses(responses) {
  const out = {};
  for (const [fieldId, resp] of Object.entries(responses || {})) {
    if (!resp || typeof resp !== 'object' || Array.isArray(resp)) {
      out[fieldId] = { sent_complete: false };
      continue;
    }
    const value = Array.isArray(resp.value)
      ? resp.value.map((item) => formatDisplayValue(item)).filter(Boolean).join(', ')
      : resp.value;
    out[fieldId] = {
      ...resp,
      sent_complete: resp.sent_complete === true,
      value: value ?? resp.value,
    };
  }
  return out;
}
